/** @jsx jsx */
import React, {
	createContext,
	type CSSProperties,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useReducer,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { IconButton } from '@atlaskit/button/new';
import type { StrictXCSSProp } from '@atlaskit/css';
import MenuExpandIcon from '@atlaskit/icon/glyph/menu-expand';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { DangerouslyHoistCssVarToDocumentRoot, HoistCssVarToLocalGrid } from './hoist-utils';
import { PanelSplitterProvider, type ResizeBounds } from './panel-splitter';

// These are "compressed" to discourage usage.
const sideNavVar = '--n_sNvw';
const asideVar = '--n_asDw';
const panelVar = '--n_pnlW';
const bannerMountedVar = '--n_bnrM';
const topBarMountedVar = '--n_tbrM';

// We aren't using template literals here because Compiled can't compiled them in platform ATM.
const contentHeightWhenFixed = `calc(100vh - var(--n_bnrM, 0px) - var(--n_tbrM, 0px))`;
const contentInsetBlockStart = `calc(var(--n_bnrM, 0px) + var(--n_tbrM, 0px))`;

// We define variables as they can change their size depending on the viewport width. That isn't needed for
// general grid item slots because, well, they just take up the size of the grid item! In this instance since
// the UNSAFE absolutely positioned sibling isn't on the grid it needs extra layout information.
// Note: THESE ARE THE SAME VALUES AS THE LEGACY PAGE LAYOUT.
// WHEN WE ELIMINATE USAGE OF THOSE VARIABLES THESE CAN BE RENAMED TO BE HASHED.
const UNSAFE_topBarVar = '--topNavigationHeight';
const UNSAFE_bannerVar = '--bannerHeight';
const UNSAFE_sideNavLayoutVar = '--leftSidebarWidth';
const UNSAFE_asideLayoutVar = '--rightSidebarWidth';
const UNSAFE_panelLayoutVar = '--rightPanelWidth';

// The following UNSAFE variables are used to absolutely position elements that aren't a child of page layout.
// Known use cases: Legacy pages rendered inside Confluence and Jira.
export const UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY = `calc(var(${UNSAFE_bannerVar}, 0px) + var(${UNSAFE_topBarVar}, 0px))`;
export const UNSAFE_MAIN_INLINE_START_FOR_LEGACY_PAGES_ONLY = `var(${UNSAFE_sideNavLayoutVar}, 0px)`;
export const UNSAFE_MAIN_INLINE_END_FOR_LEGACY_PAGES_ONLY = `calc(var(${UNSAFE_asideLayoutVar}, 0px) + var(${UNSAFE_panelLayoutVar}, 0px))`;

const panelSplitterResizingVar = '--localPanelSplitterResizingWidth';
const sideNavWidthResizeBounds: ResizeBounds = { min: '240px', max: '50vw' };

// ID of the root element that the banner and top bar slots hoist their sizes to. Only internally exported.
export const gridRootId = 'unsafe-design-system-page-layout-root';

/**
 * We define the z-indexes here so each page slot can be locally layered against each other.
 * For globally defined values such as flag, modal, etc, we can continue to
 * rely on accessing them through global means.
 */
const localSlotLayers = {
	// Panel should be above banner and top bar slos.
	panel: 3,
	// Banner and top bar should be above all other slots.
	banner: 2,
	topBar: 2,
	// Side nav should be above all other content slots.
	sideNav: 1,
};

const styles = cssMap({
	root: {
		display: 'grid',
		minHeight: '100vh',
		gridTemplateAreas: `
            "banner"
            "top-bar"
            "main"
            "aside"
       `,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: 'auto auto 1fr auto',
		'@media (min-width: 64rem)': {
			gridTemplateAreas: `
            "banner banner banner"
            "top-bar top-bar top-bar"
            "side-nav main aside"
       `,
			gridTemplateRows: 'auto auto 3fr',
			gridTemplateColumns: 'auto minmax(0,1fr) auto',
		},
		// Panel is only shown as a separate column on large viewports
		'@media (min-width: 90rem)': {
			gridTemplateAreas: `
                "banner banner banner banner"
                "top-bar top-bar top-bar top-bar"
                "side-nav main aside panel"
           `,
			gridTemplateRows: 'auto auto 3fr',
			gridTemplateColumns: 'auto minmax(0,1fr) auto auto',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> :not([data-layout-slot])': {
			// This hides any non-layout components that would otherwise be added to an implicit grid
			// track and break the page layout grid in unexpected and hilarious ways. Adding anything
			// as a child to page layout that is not a layout component is not supported.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			display: 'none !important',
		},
	},
	banner: {
		gridArea: 'banner',
		height: `var(${bannerMountedVar})`,
		insetBlockStart: 0,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.banner,
		overflow: 'hidden',
	},
	topBar: {
		display: 'grid',
		gridTemplateColumns: 'auto 1fr auto',
		paddingInline: token('space.150'),
		gap: token('space.200'),
		alignItems: 'center',
		backgroundColor: token('elevation.surface'),
		borderBlockEnd: `1px solid ${token('color.border')}`,
		boxSizing: 'border-box',
		gridArea: 'top-bar',
		height: `var(${topBarMountedVar})`,
		insetBlockStart: `var(${bannerMountedVar}, 0px)`,
		position: 'sticky',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.topBar,
		overflow: 'hidden',
	},
	sideNav: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		gridArea: 'main / aside / aside / aside',
		height: contentHeightWhenFixed,
		insetBlockStart: contentInsetBlockStart,
		position: 'sticky',
		width: '90%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.sideNav,
		'@media (min-width: 48rem)': {
			width: `var(${panelSplitterResizingVar}, var(${sideNavVar}))`,
		},
		'@media (min-width: 64rem)': {
			backgroundColor: token('elevation.surface'),
			borderInlineEnd: `1px solid ${token('color.border')}`,
			boxShadow: 'initial',
			gridArea: 'side-nav',
		},
	},
	sideNavHiddenMobileAndDesktop: {
		display: 'none',
	},
	sideNavHiddenMobileOnly: {
		display: 'none',
		'@media (min-width: 64rem)': {
			display: 'initial',
		},
	},
	sideNavHiddenDesktopOnly: {
		'@media (min-width: 64rem)': {
			display: 'none',
		},
	},
	main: {
		gridArea: 'main',
	},
	aside: {
		gridArea: 'aside',
		position: 'static',
		'@media (min-width: 64rem)': {
			width: `var(${asideVar})`,
			justifySelf: 'end',
		},
	},
	panel: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		gridArea: 'main / aside / aside / aside',
		justifySelf: 'end',
		width: `var(${panelVar})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: localSlotLayers.panel,
		'@media (min-width: 90rem)': {
			backgroundColor: token('elevation.surface'),
			borderInlineStart: `1px solid ${token('color.border')}`,
			boxShadow: 'initial',
			gridArea: 'panel',
		},
	},
	fixedContentArea: {
		insetBlockStart: contentInsetBlockStart,
		overflow: 'auto',
		position: 'sticky',
		'@media (min-width: 64rem)': {
			height: contentHeightWhenFixed,
		},
	},
});

const DangerouslyHoistSlotSizes = createContext(false);
const ToggleSideNavVisibility = createContext<{ current: null | (() => void) }>({ current: null });

function toggleReducer(state: boolean) {
	return !state;
}

export function Root({
	children,
	xcss,
	UNSAFE_dangerouslyHoistSlotSizes = false,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	UNSAFE_dangerouslyHoistSlotSizes?: boolean;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const toggleSideNavRef = useRef<() => void | null>(null);

	useEffect(() => {
		if (process.env.NODE_ENV !== 'production') {
			if (ref.current) {
				// eslint-disable-next-line no-console
				Array.from(ref.current.children).forEach((child) => {
					if (!child.hasAttribute('data-layout-slot')) {
						// eslint-disable-next-line no-console
						console.error(
							`Page Layout Error

This element has been forcibly hidden:

`,
							child,
							`

An element was rendered as a child of the page layout root that isn't a page layout component! Resolve this error by moving it into a page layout component.

This message will not be displayed in production.
`,
						);
					}
				});
			}
		}
	}, []);

	return (
		<ToggleSideNavVisibility.Provider value={toggleSideNavRef}>
			<DangerouslyHoistSlotSizes.Provider value={UNSAFE_dangerouslyHoistSlotSizes}>
				<div
					ref={ref}
					css={styles.root}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={xcss}
					id={gridRootId}
				>
					{children}
				</div>
			</DangerouslyHoistSlotSizes.Provider>
		</ToggleSideNavVisibility.Provider>
	);
}

export function SideNavButton() {
	const ref = useContext(ToggleSideNavVisibility);

	return (
		<IconButton
			appearance="subtle"
			label="Toggle Side Navigation Visibility"
			icon={MenuExpandIcon}
			onClick={() => {
				if (ref.current) {
					ref.current();
				}
			}}
		/>
	);
}

export function Banner({
	children,
	xcss,
	UNSAFE_height = 64,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	/**
	 * Not intended for long term use. This is added to support the migration to the new page layout.
	 * We intend to replace this with a banner height prop on `<Root>`.
	 */
	UNSAFE_height?: number;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div data-layout-slot css={styles.banner} className={xcss}>
			<HoistCssVarToLocalGrid variableName={bannerMountedVar} value={UNSAFE_height} />
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_bannerVar}
					value={UNSAFE_height}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			{children}
		</div>
	);
}

export function TopBar({
	children,
	xcss,
	UNSAFE_height = 48,
}: {
	children: React.ReactNode;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	/**
	 * Not intended for long term use. This is added to support the migration to the new page layout.
	 * We intend to replace this with a top bar height prop on `<Root>`.
	 */
	UNSAFE_height?: number;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<header data-layout-slot css={styles.topBar} className={xcss}>
			<HoistCssVarToLocalGrid variableName={topBarMountedVar} value={UNSAFE_height} />
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_topBarVar}
					value={UNSAFE_height}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			{children}
		</header>
	);
}

export function Panel({
	children,
	defaultWidth = 365,
}: {
	children: React.ReactNode;
	defaultWidth?: number;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	return (
		<div
			data-layout-slot
			css={[styles.panel, styles.fixedContentArea]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ [panelVar]: `${defaultWidth}px` } as CSSProperties}
		>
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_panelLayoutVar}
					value="0px"
					mediaQuery={media.above.lg}
					responsiveValue={defaultWidth}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			{children}
		</div>
	);
}

export function Main({
	children,
	isFixed,
	xcss,
}: {
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	children: React.ReactNode;
	isFixed?: boolean;
}) {
	return (
		<div
			data-layout-slot
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={xcss}
			role="main"
			css={[styles.main, isFixed && styles.fixedContentArea]}
		>
			{children}
		</div>
	);
}

/**
 * Key concept: SideNav has two visibility states, based on the viewport size.
 * On small viewports: the side nav is either hidden (not displayed at all), or visible and (forcibly) expanded.
 * On larger viewports: the side nav can be collapsed (e.g. only icons are shown), or expanded.
 */
export function SideNav({
	children,
	defaultCollapsed = false,
	label = 'main',
	defaultWidth = 320,
	testId,
}: {
	children: React.ReactNode;
	defaultCollapsed?: boolean;
	label?: string;
	defaultWidth?: number;
	testId?: string;
}) {
	const toggleSideNavVisibilityRef = useContext(ToggleSideNavVisibility);
	const [visibleOnDesktop, toggleVisibleOnDesktop] = useReducer(toggleReducer, !defaultCollapsed);
	const [visibleOnMobile, toggleVisibleOnMobile] = useReducer(toggleReducer, false);
	const [width, setWidth] = useState(defaultWidth);
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
	const panelSplitterParentRef = useRef<HTMLDivElement | null>(null);
	const sideNavVariableWidth = `clamp(${sideNavWidthResizeBounds.min}, ${width}px, ${sideNavWidthResizeBounds.max})`;
	const devTimeOnlyAttributes: Record<string, string | boolean> = {};

	if (process.env.NODE_ENV !== 'production') {
		const visible: string[] = [];
		if (visibleOnMobile) {
			visible.push('small');
		}
		if (visibleOnDesktop) {
			visible.push('large');
		}
		devTimeOnlyAttributes['data-visible'] = visible.length ? visible.join(',') : 'false';
	}

	const toggleSideNavVisibility = useCallback(() => {
		const { matches } = window.matchMedia('(min-width: 64rem)');
		if (matches) {
			toggleVisibleOnDesktop();
		} else {
			toggleVisibleOnMobile();
		}
	}, []);

	useLayoutEffect(() => {
		toggleSideNavVisibilityRef.current = toggleSideNavVisibility;
	}, [toggleSideNavVisibility, toggleSideNavVisibilityRef]);

	useEffect(() => {
		const mediaQueryList = window.matchMedia('(min-width: 64rem)');
		return bind(mediaQueryList, {
			type: 'change',
			listener() {
				if (mediaQueryList.matches) {
					// We're transitioning from tablet to desktop viewport size.
					// We forcibly show the side nav if it was shown on mobile.
					if (visibleOnMobile && !visibleOnDesktop) {
						toggleVisibleOnDesktop();
					}
				}
			},
		});
	}, [visibleOnDesktop, visibleOnMobile]);

	return (
		<nav
			{...devTimeOnlyAttributes}
			data-layout-slot
			aria-label={label}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={
				{
					[sideNavVar]: sideNavVariableWidth,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				} as CSSProperties
			}
			ref={panelSplitterParentRef}
			css={[
				styles.sideNav,
				!visibleOnMobile && visibleOnDesktop && styles.sideNavHiddenMobileOnly,
				visibleOnMobile && !visibleOnDesktop && styles.sideNavHiddenDesktopOnly,
				!visibleOnMobile && !visibleOnDesktop && styles.sideNavHiddenMobileAndDesktop,
			]}
			data-testid={testId}
		>
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_sideNavLayoutVar}
					value="0px"
					mediaQuery={media.above.md}
					responsiveValue={visibleOnDesktop ? sideNavVariableWidth : 0}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			<PanelSplitterProvider
				panelRef={panelSplitterParentRef}
				panelWidth={width}
				onCompleteResize={setWidth}
				onClick={toggleSideNavVisibility}
				resizeBounds={sideNavWidthResizeBounds}
				resizingCssVar={panelSplitterResizingVar}
			>
				{children}
			</PanelSplitterProvider>
		</nav>
	);
}

export function Aside({
	children,
	xcss,
	isFixed,
	defaultWidth = 330,
}: {
	children: React.ReactNode;
	isFixed?: boolean;
	xcss?: StrictXCSSProp<'backgroundColor', never>;
	defaultWidth?: number;
}) {
	const dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);

	return (
		<div
			data-layout-slot
			css={[styles.aside, isFixed && styles.fixedContentArea]}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={xcss}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={{ [asideVar]: `${defaultWidth}px` } as CSSProperties}
		>
			{dangerouslyHoistSlotSizes && (
				// ------ START UNSAFE STYLES ------
				// These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
				// When they aren't needed anymore we can delete them wholesale.
				<DangerouslyHoistCssVarToDocumentRoot
					variableName={UNSAFE_asideLayoutVar}
					value="0px"
					mediaQuery={media.above.md}
					responsiveValue={defaultWidth}
				/>
				// ------ END UNSAFE STYLES ------
			)}
			{children}
		</div>
	);
}
